import { Link } from 'react-router-dom';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

import { getAvatarColorClass } from '@/lib/avatarColor';

import type { Alert } from '@/types/dashboard.type';

type Props = {
  alerts: Alert[];
  loading: boolean;
};

function getInitials(name: string): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function formatMessage(message: Record<string, unknown>): string {
  const values = Object.values(message);
  if (!values.length) return '';
  return values.map(v => String(v)).join(' · ');
}

const AlertSkeleton = () => (
  <div className="flex items-center justify-between rounded-md border p-3">
    <div className="flex items-center gap-3">
      <Skeleton className="h-8 w-8 rounded-full" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-40" />
      </div>
    </div>
    <Skeleton className="h-8 w-16" />
  </div>
);

const AlertRow = ({ alert }: { alert: Alert }) => {
  const initials = getInitials(alert.module);

  return (
    <div className="flex items-center justify-between rounded-md border p-3">
      <div className="flex items-center gap-3">
        <Avatar className="h-8 w-8">
          <AvatarFallback className={getAvatarColorClass(alert.id || alert.module)}>
            {initials}
          </AvatarFallback>
        </Avatar>
        <div>
          <div className="flex items-center gap-2">
            <div className="font-medium">{alert.module}</div>
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-400/10 dark:text-yellow-400">
              {alert.type}
            </span>
          </div>
          <div className="text-xs text-muted-foreground">
            {formatMessage(alert.message)} · {alert.when}
          </div>
        </div>
      </div>
      <Button asChild variant="outline" size="sm">
        <Link to={`/calls?alert=${alert.id}`}>View</Link>
      </Button>
    </div>
  );
};

const EmptyState = () => (
  <div className="text-center py-4 text-muted-foreground">
    <p className="text-sm">No alerts at this time</p>
  </div>
);

export const AlertsList = ({ alerts, loading }: Props) => {
  return (
    <Card className="bg-muted rounded-2xl p-1 pt-0 gap-0 border-0 shadow-none flex flex-col">
      <CardHeader className="flex flex-col px-5 py-3">
        <CardTitle className="text-sm">Alerts</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 bg-card p-5 rounded-xl shadow-sm flex-1">
        {loading ? (
          ['skeleton-1', 'skeleton-2'].map(id => <AlertSkeleton key={id} />)
        ) : alerts.length > 0 ? (
          alerts.map(alert => <AlertRow key={alert.id} alert={alert} />)
        ) : (
          <EmptyState />
        )}
      </CardContent>
    </Card>
  );
};
